import { useEffect, useState, useRef } from 'react';
import { Flex } from '../../../component-library';
import { VideoChat } from '../../../components';
import { useReceiveMessage, usePostMessage } from '../../../context/messageContext';
import { useCalibration } from '../../../context/calibrationContext';
import { useToolDetection } from '../../../helpers/distance-detection-hooks';
import { calibrationConstants } from '../../../helpers/calibration-constants';
import { ReactComponent as Refractotype1 } from '../../../assets/refraction/refractotype1.svg';
import { ReactComponent as Refractotype2 } from '../../../assets/refraction/refractotype2.svg';
import { ReactComponent as Refractotype3 } from '../../../assets/refraction/refractotype3.svg';
import { ReactComponent as Refractotype4 } from '../../../assets/refraction/refractotype4.svg';
import { ReactComponent as Refractotype5 } from '../../../assets/refraction/refractotype5.svg';
import { ReactComponent as Refractotype6 } from '../../../assets/refraction/refractotype6.svg';
import { ReactComponent as Refractotype7 } from '../../../assets/refraction/refractotype7.svg';
import { ReactComponent as Refractotype8 } from '../../../assets/refraction/refractotype8.svg';
import { getLogmarLineSize500mm } from '../../../helpers/functions';

const RefractionTwo = () => {
  const {
    calibrationData: { perceivedFocalLength },
  } = useCalibration();
  const messageFromOptician = useReceiveMessage();
  const postMessage = usePostMessage();

  const [stimulusType, setStimulusType] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(1);
  const [visibleStimulus, setVisibleStimulus] = useState(false);
  const [stimulusSize, setStimulusSize] = useState(null);
  const [pixelDensity, setPixelDensity] = useState(0);
  const [stimulusSizeInPx, setStimulusSizeInPx] = useState(0);
  const [selectedRefractotype, setSelectedRefractotype] = useState(1);
  const [activeEye, setActiveEye] = useState(null);

  const canvasDOM = useRef(null);
  const videoDOM = useRef(null);
  const { toolDistance, setShouldCollectToolDistance } = useToolDetection({
    videoDOM,
    canvasDOM,
    perceivedFocalLength: perceivedFocalLength ? perceivedFocalLength : calibrationConstants.perceivedFocalLength,
  });

  const videoChatCustomStyle = {
    videoBox: {
      position: 'fixed',
      top: 0,
      left: 0,
      visibility: '',
      width: 100,
      height: 100,
      overflow: 'hidden',
    },
    localParticipant: {
      overflow: 'hidden',
    },
  };

  const updateTarget = (newTarget) => {
    setStimulusType(newTarget.stimulusType);
    setSelectedPosition(newTarget.stimulusPosition);
    setVisibleStimulus(newTarget.visibleStimulus);
    setStimulusSize(newTarget.stimulusSize);
    setPixelDensity(newTarget.pixelDensity);
    setSelectedRefractotype(newTarget.stimulusRefractotype);
    setActiveEye(newTarget.activeEye);
  };

  const getRefractotype = () => {
    switch (Number(selectedRefractotype)) {
      case 2:
        return <Refractotype2 />;
      case 3:
        return <Refractotype3 />;
      case 4:
        return <Refractotype4 />;
      case 5:
        return <Refractotype5 />;
      case 6:
        return <Refractotype6 />;
      case 7:
        return <Refractotype7 />;
      case 8:
        return <Refractotype8 />;
      default:
        return <Refractotype1 />;
    }
  };

  const getTarget = () => {
    const targetStyle = {
      width: stimulusSizeInPx,
      height: stimulusSizeInPx,
      position: 'absolute',
      top: pixelDensity ? 5 / pixelDensity : 0,
      left: window.screen.width / 2 - Math.round(stimulusSizeInPx / 2),
      // display: visibleStimulus === true ? 'block' : 'none',
      webkitTransition: 'all 1s',
      mozTransition: 'all 1s',
      transition: 'all 1s',
      transform: Number(selectedPosition) === 2 ? 'rotate(60deg)' : Number(selectedPosition) === 3 ? 'rotate(-60deg)' : '',
    };

    if (activeEye && activeEye === 'rightEye') {
      targetStyle.left += 33 / pixelDensity;
    } else if (activeEye && activeEye === 'leftEye') {
      targetStyle.left -= 33 / pixelDensity;
    }

    return <Flex style={{ ...targetStyle, backgroundColor: stimulusType === 'dot' ? '#000' : 'transparent' }}>{getRefractotype()}</Flex>;
  };

  useEffect(() => {
    switch (messageFromOptician.type) {
      case 'updateTarget':
        updateTarget(messageFromOptician.content);
        break;
      case 'detectDistance':
        setShouldCollectToolDistance(true);
        break;
      case 'stopDetections':
        setShouldCollectToolDistance(false);
        break;
      default:
        break;
    }
  }, [messageFromOptician, setShouldCollectToolDistance]);

  useEffect(() => {
    postMessage({ type: 'patientDistance', content: toolDistance });
  }, [postMessage, toolDistance]);

  useEffect(() => {
    if (stimulusSize && stimulusSize.indexOf('mm') !== -1) {
      return setStimulusSizeInPx(Math.round(Number(stimulusSize.replace('mm', '')) / pixelDensity));
    }
    const sizeFor500mm = getLogmarLineSize500mm(stimulusSize ? stimulusSize : '4');
    const contentSize = (sizeFor500mm * (toolDistance ? toolDistance : 500)) / 500;
    setStimulusSizeInPx((contentSize / pixelDensity) * 20);
  }, [selectedPosition, stimulusSize, visibleStimulus, pixelDensity]);

  return (
    <Flex>
      <VideoChat stacked customStyle={videoChatCustomStyle} />
      <Flex
        width='100vw'
        height='100vh'
        justifyContent='center'
        bg={[5, 6, 7].includes(Number(selectedRefractotype)) ? 'white' : 'black'}
        style={{ zIndex: 9 }}
      >
        <video id='video' width='100%' style={{ display: 'none' }} ref={videoDOM}>
          Video stream not available.
        </video>
        <canvas id='canvas' style={{ display: 'none' }} ref={canvasDOM}></canvas>
        {getTarget()}
      </Flex>
    </Flex>
  );
};

export default RefractionTwo;
